import { Progress, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { Asset, AssetKind } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { refusalOf, uploadFile } from "../../api/upload-flow";
import { attemptKey, lostAttempt, type LostAttempt } from "../idempotency";
import { toUserMessage } from "../messages";
import { maximumFiles } from "../../ui/format";

/** One file's upload into a brand's library, as a picker or a panel runs it. */
export interface MediaUpload {
  /**
   * Uploads the file, or says why not; null (no file chosen) does nothing. True once the file
   * is in the library, so a run of several files can stop at the first that fails.
   */
  send: (file: File | null) => Promise<boolean>;
  /**
   * Live's several files at once (`useFileUploadDialog`'s `maxFiles`): more than `room` is
   * refused with live's words and none goes up; else each in turn, stopping at the first that
   * fails.
   */
  sendAll: (files: readonly File[], room: number) => Promise<void>;
  /** A file is on its way up, from the first of a run to its last. */
  pending: boolean;
  /** The share sent, 0 to 1, while the bytes go up. */
  progress: number | null;
  problem: string | null;
}

interface MediaUploadOptions {
  teamId: string;
  brandId: string;
  /** The kinds of file it takes: an image picker takes images, the editor's Uploads all three. */
  kinds: readonly AssetKind[];
  /** The ready file, once the library's lists are read again. */
  onUploaded: (asset: Asset) => void;
  /** The upload in flight (null once it ends): closing the picker aborts it. */
  onUploading: (controller: AbortController | null) => void;
}

/** An image picker's kinds. */
export const IMAGE_ONLY: readonly AssetKind[] = ["image"];

/** A video picker's kinds (the video composer's, #260). */
export const VIDEO_ONLY: readonly AssetKind[] = ["video"];

/** An audio picker's kinds (the video composer's Audio refs, #260). */
export const AUDIO_ONLY: readonly AssetKind[] = ["audio"];

/** Every kind the library holds: the editor's Uploads. */
export const ANY_MEDIA: readonly AssetKind[] = ["image", "video", "audio"];

/** What to say when a file is not of the kinds asked for. */
const WRONG_KIND: Record<string, string> = {
  image: "Choose an image: JPEG, PNG or WebP.",
  video: "Choose a video: MP4, MOV or WebM.",
  audio: "Choose an audio: MP3 or WAV.",
};

/**
 * A file's upload through the library's one upload path (S1), for the pickers and the editor:
 * only the kinds asked for, one idempotency key per file (the same file after a lost answer
 * resends its key), the library's lists read again, then the asset. An aborted upload says
 * nothing: the flow deletes its pending asset.
 */
export function useMediaUpload(options: MediaUploadOptions): MediaUpload {
  const { teamId, brandId, kinds, onUploaded, onUploading } = options;
  const queryClient = useQueryClient();
  // Through a mutation, so an ended session reaches the shell's one handler (F3).
  const upload = useMutation({ mutationFn: uploadFile });
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [lost, setLost] = useState<LostAttempt | undefined>(undefined);
  // A run of several files: pending between one file's end and the next's start too.
  const [running, setRunning] = useState(false);

  const send = async (file: File | null) => {
    if (file === null) return false;
    const refusal = refusalOf(file);
    const kind = file.type.split("/")[0] ?? "";
    if (refusal !== undefined || !kinds.some((each) => each === kind)) {
      setProblem(
        refusal ?? WRONG_KIND[kinds.join(",")] ?? "This type of file cannot be uploaded here.",
      );
      return false;
    }
    const body = `${file.name}:${file.size}:${file.lastModified}`;
    const key = attemptKey(body, lost);
    setProblem(null);
    setProgress(0);
    const controller = new AbortController();
    onUploading(controller);
    try {
      const asset = await upload.mutateAsync({
        teamId,
        brandId,
        file,
        key,
        signal: controller.signal,
        onStep: (step) => setProgress(step.step === "sending" ? step.progress : null),
      });
      setLost(undefined);
      await refreshLibraryLists(queryClient, teamId, brandId);
      onUploaded(asset);
      return true;
    } catch (error) {
      if (controller.signal.aborted) return false;
      setProblem(toUserMessage(error));
      setLost(lostAttempt(key, body, error));
      return false;
    } finally {
      onUploading(null);
      setProgress(null);
    }
  };
  const sendAll = async (files: readonly File[], room: number) => {
    if (files.length === 0) return;
    if (files.length > room) {
      setProblem(maximumFiles(room));
      return;
    }
    setRunning(true);
    try {
      for (const file of files) {
        if (!(await send(file))) return;
      }
    } finally {
      setRunning(false);
    }
  };
  return { send, sendAll, pending: upload.isPending || running, progress, problem };
}

/** An upload's progress while it runs, and why it failed when it did. */
export function UploadState({ upload }: { upload: MediaUpload }) {
  return (
    <>
      {upload.pending ? (
        <Progress value={(upload.progress ?? 0) * 100} aria-label="Uploading" animated />
      ) : null}
      {upload.problem === null ? null : (
        <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
          {upload.problem}
        </Text>
      )}
    </>
  );
}
